/**
 * Gestión → CardPlus (`/gestion/cardplus`).
 *
 * Puerto de `Cardplus::{redemption, historical}` + sus vistas y *bundles*
 * (`src/CardPlus/{redemption,historical}.js`), que el panel anterior resolvía
 * contra el servicio externo de CardPlus con el token de la ficha de la empresa.
 *
 * Qué se porta y qué no:
 * - **Histórico**: la tabla de redenciones (`redemptions/list`) con el filtro por
 *   documento, tipo (combustible o puntos) y estado, y el enlace al ticket del
 *   servicio.
 * - **Recompensas**: el catálogo de la sucursal (`rewards/:sucursal`) con sus
 *   puntos y su stock, la **consulta de la tarjeta** (`clients/:tarjeta`) con el
 *   saldo del cliente y el **canje** (`redeem`) con confirmación previa.
 * - **Canjes locales** (`cardplus` + `pts_pump_transactions`): ya viven en
 *   Logística → Canjes CardPlus; aquí se enlazan, no se duplican.
 * - **Pendiente con candado**: el **despacho** (`Cardplus::dispatch`), que espera
 *   transacciones del surtidor y mueve la redención de `processing` a
 *   `confirmed`; necesita un despacho real para probarse.
 *
 * Reglas conservadas:
 * - Una recompensa que no está `active` no se puede canjear (el panel anterior lo
 *   avisaba antes de llamar al servicio).
 * - El canje pide confirmación diciendo cuántos puntos se descuentan.
 * - El token nunca se muestra: sólo si está vigente y cuándo caduca.
 */

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { Paginador } from '@/components/ui/paginador';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatNumber } from '@/lib/utils';
import { ExternalLink, Info, Lock, RefreshCw, Search, Stamp, Trophy } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  type FiltrosRedenciones,
  type RecompensaCardplus,
  useCanjearCardplus,
  useEstadoCardplus,
  useRecompensasCardplus,
  useRedencionesCardplus,
  useTarjetaCardplus,
} from '../cardplus.api';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Etiqueta del estado de una redención, como el histórico anterior. */
function estadoRedencion(estado: string | null): { etiqueta: string; variant: 'success' | 'warning' | 'brand' } {
  if (estado === 'confirmed') return { etiqueta: 'ENTREGADO', variant: 'success' };
  if (estado === 'processing') return { etiqueta: 'EN PROCESO', variant: 'warning' };

  return { etiqueta: 'CANJEADO', variant: 'brand' };
}

/** Mensaje legible de un error de la API. */
function mensajeDeError(error: unknown, respaldo: string): string {
  return error instanceof ApiError ? error.message : respaldo;
}

/** «1 punto» / «2 puntos». */
function puntos(cantidad: number): string {
  return `${formatNumber(cantidad)} ${cantidad === 1 ? 'punto' : 'puntos'}`;
}

/** Enlace al ticket dentro del servicio de CardPlus. */
function enlaceTicket(dominio: string, ticket: string | null): string | null {
  if (!ticket) return null;

  return `${dominio.replace(/\/api$/, '')}/redemptions?id=${encodeURIComponent(ticket)}`;
}

export function CardplusPage() {
  useDocumentTitle('Gestión · CardPlus');

  const [pestana, setPestana] = useState<'historico' | 'recompensas'>('historico');
  const [documento, setDocumento] = useState('');
  const [tipo, setTipo] = useState('');
  const [estado, setEstado] = useState('');

  const [tarjeta, setTarjeta] = useState('');
  const [tarjetaConsultada, setTarjetaConsultada] = useState('');
  const [recompensa, setRecompensa] = useState<RecompensaCardplus | null>(null);
  const [cantidad, setCantidad] = useState('1');
  const [aviso, setAviso] = useState<string | null>(null);

  const filtros = useMemo<FiltrosRedenciones>(
    () => ({ identity: documento.trim(), isFuel: tipo, status: estado }),
    [documento, tipo, estado],
  );

  const estadoConsulta = useEstadoCardplus();
  const consulta = useRedencionesCardplus(filtros);
  const recompensas = useRecompensasCardplus(true);
  const tarjetaConsulta = useTarjetaCardplus(tarjetaConsultada);
  const canjear = useCanjearCardplus();

  const datos = consulta.data;
  const dominio = recompensas.data?.dominio ?? estadoConsulta.data?.dominio ?? '';
  const redenciones = useMemo(() => datos?.redenciones ?? [], [datos]);
  const paginacion = usePaginacion(redenciones, { clave: filtros });

  const mostrar = (mensaje: string) => {
    setAviso(mensaje);
    setTimeout(() => setAviso(null), 6000);
  };

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">CardPlus</h1>
          <p className="mt-1 text-sm text-ink-500">
            Fidelización: histórico de redenciones, recompensas y canje de puntos.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={estadoConsulta.data?.habilitado ? 'success' : 'neutral'}>
            {estadoConsulta.data?.habilitado ? 'Fidelidad activa' : 'Fidelidad no configurada'}
          </Badge>
          <Button
            variant="outline"
            size="sm"
            leftIcon={
              <RefreshCw
                className={cn('size-4', consulta.isFetching && 'animate-spin')}
                aria-hidden="true"
              />
            }
            onClick={() => void consulta.refetch()}
          >
            Actualizar
          </Button>
        </div>
      </header>

      {estadoConsulta.data ? (
        <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-500">
          <span>Sucursal {estadoConsulta.data.sucursal}</span>
          <span>
            Token:{' '}
            {estadoConsulta.data.token.vigente
              ? `vigente hasta ${estadoConsulta.data.token.caduca_en?.slice(0, 10) ?? '—'}`
              : 'se renovará al consultar'}
          </span>
          <span>Credenciales de la empresa: {estadoConsulta.data.credenciales ? 'sí' : 'no'}</span>
        </p>
      ) : null}

      {datos ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {[
            { etiqueta: 'Redenciones', valor: String(datos.resumen.total), detalle: 'En el filtro actual' },
            { etiqueta: 'Entregadas', valor: String(datos.resumen.entregados), detalle: 'Confirmadas por el surtidor' },
            { etiqueta: 'En proceso', valor: String(datos.resumen.en_proceso), detalle: 'Pendientes de entrega' },
            { etiqueta: 'Puntos canjeados', valor: formatNumber(datos.resumen.puntos), detalle: 'Suma de puntos usados' },
          ].map((tarjetaResumen) => (
            <Card key={tarjetaResumen.etiqueta}>
              <CardContent className="p-4">
                <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">
                  {tarjetaResumen.etiqueta}
                </p>
                <p className="text-lg font-bold text-ink-900">{tarjetaResumen.valor}</p>
                <p className="text-xs text-ink-500">{tarjetaResumen.detalle}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : null}

      <Card>
        <CardContent className="space-y-4 p-4">
          <nav className="flex items-center gap-2 overflow-x-auto pb-1 sm:flex-wrap sm:pb-0" aria-label="Secciones de CardPlus">
            {[
              { clave: 'historico' as const, label: 'Histórico', icono: Stamp },
              { clave: 'recompensas' as const, label: 'Recompensas', icono: Trophy },
            ].map((opcion) => (
              <button
                key={opcion.clave}
                aria-current={pestana === opcion.clave ? 'page' : undefined}
                className={cn(
                  'inline-flex shrink-0 items-center gap-2 rounded-control px-3 py-2 text-xs font-semibold transition-colors',
                  pestana === opcion.clave
                    ? 'bg-ink-900 text-white'
                    : 'border border-ink-200 bg-white text-ink-700 hover:border-brand-300 hover:text-brand-700',
                )}
                onClick={() => setPestana(opcion.clave)}
              >
                <opcion.icono className="size-3.5" aria-hidden="true" />
                {opcion.label}
              </button>
            ))}

            <Link
              className="ml-auto text-xs font-semibold text-brand-700 underline-offset-2 hover:underline"
              to="/logistica/canjes-cardplus"
            >
              Canjes locales (surtidor) →
            </Link>
          </nav>

          {aviso ? (
            <Alert variant="success" title="Operación realizada">
              {aviso}
            </Alert>
          ) : null}

          {pestana === 'historico' ? (
            <>
              <div className="grid items-end gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <label className="flex flex-col gap-1 text-xs text-ink-500">
                  Documento del cliente
                  <span className="relative block">
                    <Search
                      className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-400"
                      aria-hidden="true"
                    />
                    <input
                      className={cn(CONTROL, 'pl-9')}
                      placeholder="DNI o RUC…"
                      value={documento}
                      onChange={(evento) => setDocumento(evento.target.value)}
                    />
                  </span>
                </label>

                <label className="flex flex-col gap-1 text-xs text-ink-500">
                  Tipo de recompensa
                  <Select
                    className={CONTROL}
                    value={tipo}
                    onChange={(evento) => setTipo(evento.target.value)}
                  >
                    <option value="">Todas</option>
                    <option value="1">Combustible</option>
                    <option value="0">Puntos / otros</option>
                  </Select>
                </label>

                <label className="flex flex-col gap-1 text-xs text-ink-500">
                  Estado
                  <Select
                    className={CONTROL}
                    value={estado}
                    onChange={(evento) => setEstado(evento.target.value)}
                  >
                    <option value="">Todos</option>
                    <option value="confirmed">Entregado</option>
                    <option value="processing">En proceso de entrega</option>
                  </Select>
                </label>
              </div>

              {consulta.isError ? (
                <Alert variant="error" title="No se pudo cargar el histórico de CardPlus">
                  {mensajeDeError(consulta.error, 'Inténtalo de nuevo en unos segundos.')}
                </Alert>
              ) : null}

              {consulta.isLoading ? <Skeleton className="h-64 w-full" /> : null}

              {!consulta.isLoading ? (
                <div className="max-h-[56vh] overflow-auto rounded-card border border-ink-100">
                  <table className="w-full border-collapse text-[13px]">
                    <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                      <tr className="text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
                        <th className="px-2 py-2">Cliente</th>
                        <th className="px-2 py-2">Recompensa</th>
                        <th className="px-2 py-2 text-right">Puntos</th>
                        <th className="px-2 py-2">Fecha</th>
                        <th className="px-2 py-2">Estado</th>
                        <th className="px-2 py-2 text-right">Ticket</th>
                      </tr>
                    </thead>
                    <tbody>
                      {redenciones.length === 0 ? (
                        <tr className="border-t border-ink-100">
                          <td colSpan={6} className="px-3 py-10 text-center text-sm text-ink-500">
                            No hay redenciones que cumplan los filtros.
                          </td>
                        </tr>
                      ) : null}

                      {paginacion.visibles.map((fila, indice) => {
                        const etiqueta = estadoRedencion(fila.status);
                        const ticket = enlaceTicket(dominio, fila.ticket);

                        return (
                          <tr
                            key={`${fila.ticket ?? indice}-${indice}`}
                            className="border-t border-ink-100 hover:bg-brand-50/30"
                          >
                            <td className="max-w-[8.5rem] px-2 py-2">
                              <p className="truncate font-semibold text-ink-900" title={fila.client ?? ''}>
                                {fila.client ?? '—'}
                              </p>
                              <p className="truncate text-xs text-ink-500">{fila.identity ?? ''}</p>
                            </td>
                            <td className="px-2 py-2">
                              <p className="font-medium text-ink-800">{fila.reward ?? '—'}</p>
                              <p className="text-xs text-ink-500">
                                {fila.quantity ?? '0'} {fila.isFuel === '1' ? 'gal' : 'unid.'}
                                {fila.branch ? ` · ${fila.branch}` : ''}
                              </p>
                            </td>
                            <td className="px-2 py-2 text-right font-semibold whitespace-nowrap">
                              {formatNumber(Number(fila.points_used ?? 0))}
                            </td>
                            <td className="px-2 py-2 text-xs whitespace-nowrap text-ink-600">
                              {fila.date ?? '—'}
                            </td>
                            <td className="px-2 py-2 whitespace-nowrap">
                              <Badge variant={etiqueta.variant}>{etiqueta.etiqueta}</Badge>
                            </td>
                            <td className="px-2 py-2 text-right whitespace-nowrap">
                              {ticket ? (
                                <a
                                  className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700 hover:underline"
                                  href={ticket}
                                  rel="noreferrer"
                                  target="_blank"
                                >
                                  Ver ticket
                                  <ExternalLink className="size-3.5" aria-hidden="true" />
                                </a>
                              ) : (
                                '—'
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : null}

              {!consulta.isLoading ? (
                <Paginador {...paginacion} etiqueta="redenciones" />
              ) : null}
            </>
          ) : null}

          {pestana === 'recompensas' ? (
            <>
              <div className="flex flex-wrap items-end gap-3">
                <label className="flex min-w-[16rem] flex-1 flex-col gap-1 text-xs text-ink-500">
                  Tarjeta CardPlus
                  <input
                    className={CONTROL}
                    placeholder="Código de la tarjeta del cliente…"
                    value={tarjeta}
                    onChange={(evento) => setTarjeta(evento.target.value)}
                  />
                </label>
                <Button
                  leftIcon={<Search className="size-4" aria-hidden="true" />}
                  disabled={tarjeta.trim() === ''}
                  onClick={() => setTarjetaConsultada(tarjeta.trim())}
                >
                  Consultar tarjeta
                </Button>
              </div>

              {tarjetaConsultada ? (
                tarjetaConsulta.isLoading ? (
                  <Skeleton className="h-20 w-full" />
                ) : tarjetaConsulta.isError ? (
                  <Alert variant="error" title="No se pudo consultar la tarjeta">
                    {mensajeDeError(tarjetaConsulta.error, 'Revisa el código de la tarjeta.')}
                  </Alert>
                ) : tarjetaConsulta.data?.cliente ? (
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-card border border-ink-200 bg-ink-50/60 p-3 text-sm">
                    <div>
                      <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">
                        Cliente
                      </p>
                      <p className="font-semibold text-ink-900">
                        {tarjetaConsulta.data.cliente.nombre ?? '—'}
                      </p>
                      <p className="text-xs text-ink-500">
                        {tarjetaConsulta.data.cliente.documento ?? ''} ·{' '}
                        {tarjetaConsulta.data.cliente.codigo ?? tarjetaConsultada}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">
                        Saldo
                      </p>
                      <p className="font-bold text-ink-900">
                        {formatNumber(tarjetaConsulta.data.cliente.puntos)} pts
                      </p>
                    </div>
                    <div className="ml-auto">
                      {recompensa ? (
                        <p className="text-xs text-ink-600">
                          Recompensa elegida: <strong>{recompensa.nombre}</strong> (
                          {recompensa.puntos} pts)
                        </p>
                      ) : (
                        <p className="text-xs text-ink-500">
                          Elige una recompensa para hacer el canje.
                        </p>
                      )}
                    </div>
                  </div>
                ) : null
              ) : null}

              {recompensas.isError ? (
                <Alert variant="error" title="No se pudieron cargar las recompensas">
                  {mensajeDeError(recompensas.error, 'Inténtalo de nuevo en unos segundos.')}
                </Alert>
              ) : null}

              {recompensas.isLoading ? <Skeleton className="h-40 w-full" /> : null}

              {!recompensas.isLoading ? (
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {(recompensas.data?.recompensas ?? []).map((item) => (
                    <button
                      key={item.id ?? item.nombre ?? ''}
                      className={cn(
                        'flex flex-col gap-2 rounded-card border p-3 text-left transition-colors',
                        recompensa?.id === item.id
                          ? 'border-brand-400 bg-brand-50/60'
                          : 'border-ink-200 bg-white hover:border-brand-300',
                      )}
                      onClick={() => setRecompensa(item)}
                    >
                      <span className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-ink-900">{item.nombre ?? '—'}</span>
                        <Badge variant="brand">{formatNumber(item.puntos)} pts</Badge>
                      </span>
                      <span className="text-xs text-ink-500">{item.descripcion ?? ''}</span>
                      <span className="flex items-center justify-between text-xs">
                        <span className="text-ink-500">Stock: {item.stock}</span>
                        <Badge variant={item.estado === 'active' ? 'success' : 'neutral'}>
                          {item.estado === 'active' ? 'DISPONIBLE' : 'NO DISPONIBLE'}
                        </Badge>
                      </span>
                    </button>
                  ))}
                </div>
              ) : null}
            </>
          ) : null}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={recompensa !== null}
        title={`¿Canjear ${recompensa?.nombre ?? ''}?`}
        description={`Se descontarán ${puntos(
          (recompensa?.puntos ?? 0) * Number(cantidad || 1),
        )} CardPlus del cliente de la tarjeta consultada.`}
        confirmLabel="Sí, canjear"
        tone="danger"
        loading={canjear.isPending}
        onClose={() => setRecompensa(null)}
        onConfirm={() => {
          const elegida = recompensa;
          const cliente = tarjetaConsulta.data?.cliente?.id ?? null;

          setRecompensa(null);

          if (!elegida?.id) return;

          if (!cliente) {
            mostrar('Consulta primero la tarjeta del cliente para conocer su identificador.');

            return;
          }

          canjear.mutate(
            {
              client: cliente,
              reward: elegida.id,
              quantity: Number(cantidad || 1),
              estado: elegida.estado ?? undefined,
            },
            {
              onSuccess: (respuesta) => mostrar(respuesta.mensaje),
              onError: (error) =>
                mostrar(mensajeDeError(error, 'No se pudo registrar el canje en CardPlus.')),
            },
          );
        }}
      >
        <label className="flex flex-col gap-1 text-xs text-ink-500">
          Cantidad
          <input
            className={CONTROL}
            inputMode="numeric"
            min={1}
            type="number"
            value={cantidad}
            onChange={(evento) => setCantidad(evento.target.value)}
          />
        </label>
      </ConfirmDialog>

      <Alert variant="info" title="Pendiente de migrar en CardPlus">
        <ul className="mt-1 ml-5 list-disc space-y-1 text-sm">
          <li>
            <span className={ICONO_EN_ALERTA}>
              <Lock className="size-3.5" aria-hidden="true" />
              El despacho del canje: espera la transacción del surtidor y mueve la redención de en
              proceso a entregado. Necesita un despacho real para probarse.
            </span>
          </li>
          <li>
            <span className={ICONO_EN_ALERTA}>
              <Lock className="size-3.5" aria-hidden="true" />
              La emisión de la tarjeta y el alta del cliente en CardPlus desde el alta de clientes.
            </span>
          </li>
        </ul>
      </Alert>

      <p className="flex items-start gap-2 text-xs text-ink-500">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        El token de CardPlus se guarda en la ficha de la empresa y se renueva solo cuando el
        servicio lo rechaza. Los canjes locales del surtidor se ven en Logística → Canjes CardPlus.
      </p>
    </div>
  );
}
